/** One color ramp of a white-label brand palette. */
interface WhiteLabelPaletteColor {
  dark?: string;
  main?: string;
  base?: string;
  medium?: string;
  light2?: string;
  light?: string;
  contrastText?: string;
}

/** Brand palette shipped per white-label clinic. */
interface WhiteLabelPaletteOptions {
  primary: WhiteLabelPaletteColor & { dark: string; main: string };
  secondary?: WhiteLabelPaletteColor;
  success?: WhiteLabelPaletteColor;
  error?: WhiteLabelPaletteColor;
  warning?: WhiteLabelPaletteColor;
  info?: WhiteLabelPaletteColor;
}

interface Image {
  url: string;
  width?: number;
  height?: number;
}

export type WhiteLabelConfigType = {
  name: string;
  url: string;
  logoUrl: Image;
  faviconUrl: string;
  title: string;
  palette: WhiteLabelPaletteOptions;
  mobile: {
    ios: string;
    android: string;
  };
  supportEmail: string;
  questionnaireLogo: Image;
  questionnaireSuccessImage?: Image;
  contrastText?: boolean;
};

const sportsMedConfig: WhiteLabelConfigType = {
  name: "Sports-Med",
  url: "https://sports-med.pl",
  logoUrl: {
    url: "/static/sports-med/wideLogo.png",
    width: 137,
    height: 31,
  },
  faviconUrl: "/static/whitelabelFavicons/sports-med.jpg",
  title: "Sports-Med",
  questionnaireLogo: {
    url: "/static/sports-med/bigLogo.png",
    width: 123,
    height: 100,
  },
  palette: {
    primary: {
      dark: "#041E47",
      main: "#314DCC",
      base: "#98A6E5",
      medium: "#DDE3F3",
      light2: "#DDE3F3",
      light: "#F1F3F9",
    },
  },
  mobile: {
    ios: "https://apps.apple.com/us/app/sports-med/id6754554983",
    android: "https://play.google.com/store/apps/details?id=pl.sportsmed",
  },
  supportEmail: "lider@sports-med.pl",
};

const myTrelaConfig: WhiteLabelConfigType = {
  name: "mytrela",
  url: "https://mytrela.com",
  logoUrl: {
    url: "https://alloweat-files-prod.s3.eu-central-1.amazonaws.com/media/4137/system/mail_logo_head.png",
  },
  faviconUrl: "/static/whitelabelFavicons/MyTrela.png",
  title: "MyTrela",
  questionnaireLogo: {
    url: "https://d1wxteijfk37ar.cloudfront.net/media/5245/system/mytrela_logo.png",
  },
  palette: {
    primary: {
      dark: "#2F1E66",
      main: "#7B71B2",
      base: "#B2B4EB",
      medium: "#D9E3FF",
      light2: "#D9E3FF",
      light: "#F1F1FA",
    },
    success: {
      dark: "#196953",
      main: "#4BB179",
      light: "#F1FDF5",
    },
  },
  mobile: {
    ios: "https://apps.apple.com/us/app/mytrela-twoja-dieta-tw%C3%B3j-plan/id6744463199",
    android: "https://play.google.com/store/apps/details?id=com.mytrela",
  },
  supportEmail: "hi@mytrela.com",
};

const bonneSanteConfig: WhiteLabelConfigType = {
  name: "bonne-sante",
  url: "https://bonne-sante.pl",
  logoUrl: {
    url: "https://alloweat-files-prod.s3.eu-central-1.amazonaws.com/media/4643/system/head_logo_pstphuindr.png",
    width: 101,
    height: 12,
  },
  faviconUrl: "/static/whitelabelFavicons/bonne-sante.png",
  title: "Bonne Santé",
  questionnaireLogo: {
    url: "https://alloweat-files-prod.s3.eu-central-1.amazonaws.com/media/4643/system/logo_qxxojslfsq.png",
    width: 140,
    height: 140,
  },
  palette: {
    primary: {
      dark: "#2B4429", // Bonne_Sante_Primary 400
      main: "#2B4429", // Bonne_Sante_Primary 400 (base)
      base: "#D4EBCA", // Bonne_Sante_Primary 200 (medium)
      medium: "#D4EBCA", // Bonne_Sante_Primary 200 (light)
      light2: "#D4EBCA", // Bonne_Sante_Primary 200 (light)
      light: "#DAEAD8", // Bonne_Sante_Primary 100 (ultra light)
    },
    secondary: {
      main: "#0F2645", // Bonne_Sante_Secondary 400
      dark: "#1E3A5F", // Bonne_Sante_Secondary 300
      light: "#7A9AC4", // Bonne_Sante_Secondary 200
      contrastText: "#FFFFFF",
    },
    error: {
      dark: "#F05A51", // Bonne_Sante_Accent 400
      main: "#F07069", // Bonne_Sante_Accent 300
      light: "#EFCAC8", // Bonne_Sante_Accent 200
    },
    success: {
      dark: "#2B4429", // Bonne_Sante_Primary 400
      main: "#9AC088", // Bonne_Sante_Primary 300
      medium: "#D4EBCA", // Bonne_Sante_Primary 200
      light: "#DAEAD8", // Bonne_Sante_Primary 100
    },
    info: {
      dark: "#0F2645", // Bonne_Sante_Secondary 400
      main: "#1E3A5F", // Bonne_Sante_Secondary 300
      light: "#CBDEF7", // Bonne_Sante_Secondary 100
    },
    warning: {
      dark: "#A88732", // Bonne_Sante_Highlight 500
      main: "#C2A14D", // Bonne_Sante_Highlight 400
      medium: "#F5D993", // Bonne_Sante_Highlight 300
      light: "#FFF9E8", // Bonne_Sante_Highlight 200
    },
  },
  mobile: {
    ios: "https://apps.apple.com/us/app/bonne-sant%C3%A9/id6748518794",
    android: "https://play.google.com/store/apps/details?id=com.bonnesante",
  },
  supportEmail: "biuro@bonne-sante.pl",
};

const twoimTempemConfig: WhiteLabelConfigType = {
  name: "Twoim Tempem",
  url: "https://twoimtempem.pl",
  logoUrl: {
    url: "/static/twoimtempem/wideLogo.png",
    width: 140,
    height: 24,
  },
  faviconUrl: "/static/whitelabelFavicons/twoimtempem.png",
  title: "Twoim Tempem",
  questionnaireLogo: {
    url: "/static/twoimtempem/bigLogo.png",
    width: 137.59,
    height: 64,
  },
  palette: {
    primary: {
      dark: "#E56F5A",
      main: "#F4B1A4",
      base: "#F6C4BB",
      medium: "#F9D8D2",
      light2: "#F9D8D2",
      light: "#FCEBE8",
    },
  },
  mobile: {
    ios: "https://apps.apple.com/us/app/twoim-tempem/id6754689410",
    android: "https://play.google.com/store/apps/details?id=pl.twoimtempem",
  },
  supportEmail: "kontakt@twoimtempem.pl",
  contrastText: true,
};

const fitpatkaConfig: WhiteLabelConfigType = {
  name: "FitPatka",
  url: "https://fitpatka.com",
  logoUrl: {
    url: "/static/fitpatka/wideLogo.png",
    width: 93,
    height: 24,
  },
  faviconUrl: "/static/whitelabelFavicons/fitpatka.png",
  title: "FitPatka",
  questionnaireLogo: {
    url: "/static/fitpatka/bigLogo.png",
    width: 238,
    height: 238,
  },
  questionnaireSuccessImage: {
    url: "/static/fitpatka/success.png",
    width: 180,
    height: 180,
  },
  palette: {
    primary: {
      dark: "#C93466",
      main: "#E46891",
      base: "#E986A7",
      medium: "#F4C3D3",
      light2: "#F4C3D3",
      light: "#FCF0F4",
      contrastText: "#041326",
    },
  },
  contrastText: true,
  mobile: {
    ios: "https://apps.apple.com/us/app/fitpatka/id6756366134",
    android: "https://play.google.com/store/apps/details?id=com.fitpatka&hl=pl",
  },
  supportEmail: "kontakt@fitpatka.com",
};

const fitbyFoodieConfig: WhiteLabelConfigType = {
  name: "Fitby Foodie",
  url: "https://fitby.pl",
  logoUrl: {
    url: "/static/fitby/wideLogo.png",
    width: 107,
    height: 35,
  },
  faviconUrl: "/static/whitelabelFavicons/fitby.png",
  title: "Fitby Foodie",
  questionnaireLogo: {
    url: "/static/fitby/bigLogo.png",
    width: 258.5,
    height: 109,
  },
  questionnaireSuccessImage: {
    url: "/static/fitby/success.png",
    width: 180,
    height: 180,
  },
  palette: {
    primary: {
      dark: "#6122A6", // color-primary-dark
      main: "#7F2DD8", // color-primary-base (kolor marki / akcja)
      base: "#D8B5FF", // color-primary-medium
      medium: "#F3E8FF", // color-primary-light
      light2: "#F3E8FF", // color-primary-light
      light: "#FBF7FF", // color-primary-ultra-light (tło strony)
    },
  },
  mobile: {
    ios: "https://apps.apple.com/us/app/fitby-foodie/id6763506012",
    android: "https://play.google.com/store/apps/details?id=pl.fitby&hl=pl",
  },
  supportEmail: "kontakt@fitby.pl",
};

const WhiteLabelConfig: Record<string, WhiteLabelConfigType> = {
  "whitelabelalloweat.pl": sportsMedConfig,
  "sports-med.com": sportsMedConfig,
  "sports-med.pl": sportsMedConfig,
  "mytrela.com": myTrelaConfig,
  "bonne-sante.pl": bonneSanteConfig,
  "twoimtempem.pl": twoimTempemConfig,
  "fitpatka.com": fitpatkaConfig,
  "fitby.pl": fitbyFoodieConfig,
};

export const getConfig = (
  key: string,
): (typeof WhiteLabelConfig)[keyof typeof WhiteLabelConfig] | null => {
  if (key in WhiteLabelConfig) {
    return WhiteLabelConfig[key as keyof typeof WhiteLabelConfig];
  }
  return null;
};
